import { pickNextRandomColor } from '@/utils/functions';
import React, {
  createContext,
  useContext,
  useState,
  useEffect
} from 'react';
import { toast } from 'react-toastify';

if (!toast.POSITION) {
  toast.POSITION = {
    TOP_CENTER: 'top-center',
    TOP_LEFT: 'top-left',
    TOP_RIGHT: 'top-right',
    BOTTOM_CENTER: 'bottom-center',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_RIGHT: 'bottom-right',
  };
}

const UiContext = createContext();

function UiProvider ({ children }) {
  // whatever you set here as default, will be used in both server side and client as well
  const [providerData, setProviderData] = useState({
    ready: false,
    commands: {
      // we may add default commands here
      // "Foo ooo OOO": {
      //   name: "Foo ooo OOO!!",
      //   id: 1,
      //   description: "The classic foo bar baz sample",
      //   color: 'red',
      //   category: 'Command',
      //   command() {alert('foo');},
      // },
    },
    data: {
      notificationBar: { open: false, list: {} },
      mainMenu: { open: false },
      // add default data to your provider here
    },
    commandPalette: { open: false },
  });

  // you can get extra data asynchronously if needed
  // this will only affect the client side
  function fetchProviderData () {
    setProviderData({
      ...providerData,
      ready: true,
    });
  }

  useEffect(() => { fetchProviderData(); }, []);

  const value = {
    ...providerData,
    setData (key, value) {
      setProviderData({
        ...providerData,
        data: {
          ...providerData.data,
          [key]: value
        }
      });
    },
    addCommands (commandsList) {
      if (!Array.isArray(commandsList)) {
        commandsList = [commandsList];
      }

      setProviderData(currentProvData => {
        const newProvData = {...currentProvData};
        newProvData.commands = newProvData.commands || {};
        commandsList.forEach(command => {
          if (!newProvData.commands[command.name]) {
            if (command.category && !command.color) {
              command.color = pickNextRandomColor();
            }
            newProvData.commands[command.name] = command;
          }
        });
        return newProvData;
      });
    }
  };

  value.toggleNotifcationBar = function toggleNotifcationBar (force) {
    setProviderData(currentData => {
      const newData = { ...currentData };
      if (force === true || force === false) {
        newData.data.notificationBar.open = force;
      } else {
        newData.data.notificationBar.open = !newData.data.notificationBar.open;
      }
      return newData;
    });
  };

  value.toggleMainMenu = function toggleMainMenu (force, options = {}) {
    setProviderData(currentData => {
      const newData = { ...currentData };
      if (force === true || force === false) {
        newData.data.mainMenu.open = force;
      } else {
        newData.data.mainMenu.open = !newData.data.mainMenu.open;
      }
      newData.data.mainMenu.options = options;
      return newData;
    });
  };

  value.togglePaletteOpen = function togglePaletteOpen (force, options = {}) {
    setProviderData(currentData => {
      const newData = { ...currentData };
      if (force === true || force === false) {
        newData.commandPalette.open = force;
      } else {
        newData.commandPalette.open = !newData.commandPalette.open;
      }
      newData.commandPalette.options = options;
      return newData;
    });
  };

  value.addNotification = function addNotification (data) {
    if (!data.id) {
      data.id = data.title + (new Date()).getTime();
    }

    setProviderData(currentData => {
      const newData = { ...currentData };
      newData.data.notificationBar.list[data.id] = data;
      return newData;
    });
  };

  value.clearNotification = function clearNotification (id) {
    setProviderData(currentData => {
      const newData = { ...currentData };
      delete newData.data.notificationBar.list[id];
      return newData;
    });
  };

  return (
    <UiContext.Provider value={value}>
      {children}
    </UiContext.Provider>
  );
}

// this allows you to use it as a simpler hook
function useUi () {
  const context = useContext(UiContext);
  if (context === undefined) {
    throw new Error('useUi must be used within a UiProvider');
  }

  return context;
}

export {
  UiProvider,
  UiContext,
  toast,
  useUi
};
